/**
 * HUD（LP・ターン・フェイズ）の値をイベント列から畳み込み、ある時刻の表示値を返す（Issue #343）。
 *
 * turn / turn_player / phase は core のイベントの外枠に毎回載っているのでそのまま使う。
 * LP は外枠に無いので、開始 LP から damage / pay_lpcost（減算）・recover（加算）・lpupdate（絶対値）で畳み込む。
 * LP の増減アニメーションは旧 Remotion 版（削除済み）の lifeForPlayer を ms 単位で移植したもの。
 * **時刻は引数の frameMs だけ**（書き出しで 1 フレームずつ撮っても同じ絵になる）。
 */
export const LIFE_TWEEN_MS = 600;     // 18 フレーム @30fps
export const GHOST_DELAY_MS = 333;    // 10 フレーム
export const GHOST_TWEEN_MS = 1200;   // 36 フレーム
export const DEFAULT_START_LP = 8000;


export const PHASE_LABEL = {
  draw: 'Draw Phase', standby: 'Standby Phase', main1: 'Main Phase',   // 実写動画と同じ表記（メイン2 だけ番号を付ける）
  battle_start: 'Battle Phase', battle_step: 'Battle Phase', damage: 'Battle Phase', damage_calc: 'Battle Phase',
  battle: 'Battle Phase', main2: 'Main Phase 2', end: 'End Phase',
};
export const phaseLabel = (phase) => (phase ? PHASE_LABEL[phase] ?? phase : '');

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const easeOutCubic = (t) => 1 - (1 - t) ** 3;

/**
 * **イベントの時刻でそのまま畳む**（Issue #449。演出に連動させる `progressMs` / `lpMs` / `linkProgressToClips` は廃止＝
 * イベント自身が時刻を持つので、ターン・フェイズ・LP はそのイベントの時刻に変わる）。
 * @param rows alignEvents().rows（並び順で ms が単調非減少）
 * @returns {{phaseSteps: Array<{ms, turn, turnPlayer, phase}>, lpChanges: [[{ms,from,to}], [...]], winSteps: [{ms, winner}], startLp, firstPlayer}}
 */
export function buildHudTimeline(rows = [], { startLp = DEFAULT_START_LP, firstPlayer = 0 } = {}) {
  const phaseSteps = [];
  const lpChanges = [[], []];
  const winSteps = [];
  let cur = { turn: 0, turnPlayer: firstPlayer, phase: null };
  const lp = [startLp, startLp];
  let lastProgressMs = 0;
  const lastLpMs = [0, 0];
  rows.forEach((row) => {
    const { event: ev, ms } = row;
    const next = { ...cur };
    if (Number.isInteger(ev.turn)) next.turn = ev.turn;
    if (ev.turn_player === 0 || ev.turn_player === 1) next.turnPlayer = ev.turn_player;
    if (ev.phase) next.phase = ev.phase;
    if (next.turn !== cur.turn || next.turnPlayer !== cur.turnPlayer || next.phase !== cur.phase) {
      // イベントの時刻は並び順で単調だが、念のため手前より前には戻さない
      const at = Math.max(lastProgressMs, ms);
      lastProgressMs = at;
      phaseSteps.push({ ms: at, ...next });
      cur = next;
    }
    const d = ev.data ?? {};
    const p = d.player;
    if (ev.kind === 'event' && (p === 0 || p === 1) && Number.isFinite(d.value)) {
      let to = lp[p];
      if (ev.type === 'damage' || ev.type === 'pay_lpcost') to = Math.max(0, lp[p] - d.value);
      else if (ev.type === 'recover') to = lp[p] + d.value;
      else if (ev.type === 'lpupdate') to = d.value;
      if (to !== lp[p]) {
        // LP もそのイベントの時刻に変わる。同じプレイヤーの手前の変化より前には戻さない
        const at = Math.max(lastLpMs[p], ms);
        lastLpMs[p] = at;
        lpChanges[p].push({ ms: at, from: lp[p], to }); lp[p] = to;
      }
    }
    if (ev.type === 'win' && d.winner != null) winSteps.push({ ms, winner: d.winner });
  });
  return { phaseSteps, lpChanges, winSteps, startLp, firstPlayer };
}

/** ms 以前で最後の要素の添字（無ければ -1）。list は ms 昇順。 */
export function lastIndexAtOrBefore(list, ms) {
  let lo = 0; let hi = list.length - 1; let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (list[mid].ms <= ms) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found;
}

/** 表示用の LP（増減アニメーション込み）。最後に LP が変わった瞬間を起点にする（間に別のイベントが挟まっても切れない）。 */
export function lifeAt(changes, ms, { tweenMs = LIFE_TWEEN_MS, delayMs = 0, startLp = DEFAULT_START_LP } = {}) {
  const i = lastIndexAtOrBefore(changes, ms);
  if (i < 0) return startLp;
  const c = changes[i];
  if (tweenMs <= 0) return c.to;
  const t = easeOutCubic(clamp01((ms - c.ms - delayMs) / tweenMs));
  return c.from + (c.to - c.from) * t;
}

/** frameMs 時点の HUD の表示値。 */
export function hudStateAt(frameMs, tl) {
  const i = lastIndexAtOrBefore(tl.phaseSteps, frameMs);
  const s = i >= 0 ? tl.phaseSteps[i] : { turn: 0, turnPlayer: tl.firstPlayer ?? 0, phase: null };
  const w = lastIndexAtOrBefore(tl.winSteps, frameMs);
  const lpOf = (opts) => [0, 1].map((p) => lifeAt(tl.lpChanges[p], frameMs, { startLp: tl.startLp, ...opts }));
  return {
    turn: s.turn, turnPlayer: s.turnPlayer, phase: s.phase, phaseLabel: phaseLabel(s.phase),
    winner: w >= 0 ? tl.winSteps[w].winner : null,
    lp: lpOf({ tweenMs: 0 }),
    lpDisplay: lpOf({}),
    lpGhost: lpOf({ tweenMs: GHOST_TWEEN_MS, delayMs: GHOST_DELAY_MS }),
    /** 最後の LP の変化の**前の値**（演出が「減る前の LP を見せる」ために読む。Issue #554） */
    lpFrom: [0, 1].map((p) => { const j = lastIndexAtOrBefore(tl.lpChanges[p], frameMs); return j >= 0 ? tl.lpChanges[p][j].from : tl.startLp; }),
  };
}

/** バーの色の段。 */
export const LIFE_BAR_MAX = 8000;
export function lifeClass(life) {
  if (life >= 8000) return 'is-max';
  if (life >= 5000) return 'is-high';
  if (life >= 2000) return 'is-medium';
  return 'is-low';
}
